/**
 * Cliente HTTP baseado em fetch nativo, com erros no formato
 * `{ response: { status, data }, config }` para alinhar ao tratamento já usado nas views.
 * Não usar rivia-fetch no bundle do browser.
 */

function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url || "");
}

function joinBaseURL(baseURL, path) {
  if (!path) return baseURL || "";
  if (isAbsoluteUrl(path)) return path;
  const base = (baseURL || "").endsWith("/")
    ? (baseURL || "").slice(0, -1)
    : baseURL || "";
  const p = path.startsWith("/") ? path.slice(1) : path;
  if (!base) return `/${p}`;
  return `${base}/${p}`;
}

function appendParams(url, params) {
  if (!params || typeof params !== "object") return url;
  const search = new URLSearchParams();
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v === undefined || v === null) return;
    if (Array.isArray(v)) {
      v.forEach((item) => search.append(k, String(item)));
    } else {
      search.append(k, String(v));
    }
  });
  const q = search.toString();
  if (!q) return url;
  return url + (url.includes("?") ? "&" : "?") + q;
}

function headersToObject(headers) {
  const o = {};
  if (!headers) return o;
  if (typeof headers.forEach === "function") {
    headers.forEach((value, key) => {
      o[key] = value;
    });
    return o;
  }
  return { ...headers };
}

function mergeHeaders(...parts) {
  const out = {};
  parts.forEach((h) => {
    if (!h || typeof h !== "object") return;
    Object.keys(h).forEach((k) => {
      const v = h[k];
      if (v !== undefined && v !== null) out[k] = v;
    });
  });
  return out;
}

// Tenta JSON para qualquer Content-Type: a API responde JSON sem declarar o
// header em parte dos erros. Corpo que não for JSON volta como texto.
async function parseResponseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function normalizeConfig(config) {
  if (config === null || config === undefined) return {};
  if (typeof config !== "object" || Array.isArray(config)) return {};
  return { ...config };
}

/**
 * @typedef {Object} CreateHttpClientOptions
 * @property {string} [baseURL]
 * @property {number} [timeoutMs] default timeout (ms), 0 = desligado
 * @property {Record<string, string>} [defaultHeaders]
 * @property {(cfg: InternalConfig) => InternalConfig | Promise<InternalConfig>} [beforeRequest]
 * @property {(ctx: { data: any, status: number, statusText: string, headers: Record<string, string>, config: any }) => void | Promise<void>} [afterSuccess]
 * @property {(ctx: { error: Error, config?: any }) => void | Promise<void>} [afterError]
 */

/**
 * @param {CreateHttpClientOptions} options
 */
export function createHttpClient(options = {}) {
  const {
    baseURL = "",
    timeoutMs = 0,
    defaultHeaders = {},
    beforeRequest,
    afterSuccess,
    afterError,
  } = options;

  /**
   * @typedef {Object} InternalConfig
   * @property {string} method
   * @property {string} url
   * @property {Record<string, string>} headers
   * @property {Record<string, any>} [params]
   * @property {any} [data]
   * @property {AbortSignal} [signal]
   * @property {number} [timeout]
   */

  async function request(method, url, data, userConfig) {
    const cfg = normalizeConfig(userConfig);
    const timeout =
      cfg.timeout !== undefined && cfg.timeout !== null ? cfg.timeout : timeoutMs;

    const m = method.toUpperCase();
    const bodySource =
      m === "DELETE"
        ? cfg.data
        : data !== undefined
          ? data
          : cfg.data;

    let internal = {
      method: m,
      url: joinBaseURL(baseURL, url),
      headers: mergeHeaders(defaultHeaders, cfg.headers),
      params: cfg.params,
      data: bodySource,
      signal: cfg.signal,
      timeout,
    };

    if (beforeRequest) {
      internal = await beforeRequest(internal);
    }

    internal.headers = mergeHeaders(internal.headers);
    internal.url = appendParams(internal.url, internal.params);

    const controller = new AbortController();
    const userSignal = internal.signal;
    if (userSignal) {
      if (userSignal.aborted) controller.abort();
      else
        userSignal.addEventListener("abort", () => controller.abort(), {
          once: true,
        });
    }

    let timeoutId;
    if (internal.timeout > 0) {
      timeoutId = setTimeout(() => controller.abort(), internal.timeout);
    }

    const methodUpper = internal.method;
    let body;
    if (methodUpper !== "GET" && methodUpper !== "HEAD") {
      const payload = internal.data;
      if (payload !== undefined && payload !== null) {
        if (
          typeof payload === "object" &&
          !(payload instanceof FormData) &&
          !(payload instanceof Blob)
        ) {
          internal.headers = mergeHeaders(internal.headers, {
            "Content-Type":
              internal.headers["Content-Type"] ||
              internal.headers["content-type"] ||
              "application/json",
          });
          body = JSON.stringify(payload);
        } else {
          body = payload;
        }
      }
    }

    // Sem corpo o header não se aplica, e com FormData só o browser sabe escrever
    // o boundary — os dois casos removem Content-Type, como o adapter xhr do axios.
    if (body === undefined || body instanceof FormData) {
      Object.keys(internal.headers).forEach((key) => {
        if (key.toLowerCase() === "content-type") delete internal.headers[key];
      });
    }

    const fetchInit = {
      method: methodUpper,
      headers: internal.headers,
      body: body === undefined ? undefined : body,
      signal: controller.signal,
    };

    const snapshotConfig = {
      url: internal.url,
      method: methodUpper,
      headers: { ...internal.headers },
      params: cfg.params,
      data:
        methodUpper === "DELETE"
          ? cfg.data
          : data !== undefined
            ? data
            : cfg.data,
      timeout: internal.timeout,
    };

    try {
      let response;
      try {
        response = await fetch(internal.url, fetchInit);
      } catch (e) {
        if (timeoutId) clearTimeout(timeoutId);
        if (
          e.name === "AbortError" ||
          (e instanceof DOMException && e.name === "AbortError")
        ) {
          const abortedByUser = userSignal && userSignal.aborted;
          const err = new Error(
            abortedByUser ? "canceled" : `timeout of ${internal.timeout}ms exceeded`
          );
          err.name = "AbortError";
          err.code = abortedByUser ? "ERR_CANCELED" : "ECONNABORTED";
          err.isHttpClientError = true;
          err.config = snapshotConfig;
          if (afterError) await afterError({ error: err, config: snapshotConfig });
          throw err;
        }
        const err = new Error(e.message || "Network Error");
        err.code = "ERR_NETWORK";
        err.isHttpClientError = true;
        err.config = snapshotConfig;
        if (afterError) await afterError({ error: err, config: snapshotConfig });
        throw err;
      }
      if (timeoutId) clearTimeout(timeoutId);

      const responseHeaders = headersToObject(response.headers);
      const parsedData = await parseResponseBody(response);

      if (!response.ok) {
        const httpErr = new Error(
          `Request failed with status code ${response.status}`
        );
        httpErr.name = "HttpError";
        httpErr.isHttpClientError = true;
        httpErr.response = {
          status: response.status,
          statusText: response.statusText,
          data: parsedData,
          headers: responseHeaders,
        };
        httpErr.config = snapshotConfig;
        if (afterError) await afterError({ error: httpErr, config: snapshotConfig });
        throw httpErr;
      }

      const result = {
        data: parsedData,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        config: snapshotConfig,
      };

      if (afterSuccess) await afterSuccess(result);
      return result;
    } catch (e) {
      if (timeoutId) clearTimeout(timeoutId);
      throw e;
    }
  }

  return {
    get: (url, config) => request("GET", url, undefined, config),
    post: (url, data, config) => request("POST", url, data, config),
    put: (url, data, config) => request("PUT", url, data, config),
    patch: (url, data, config) => request("PATCH", url, data, config),
    delete: (url, config) => request("DELETE", url, undefined, config),
  };
}

